(function () {
  'use strict';

  function debounce(callback, wait) {
    var timer;
    return function () {
      var args = arguments;
      window.clearTimeout(timer);
      timer = window.setTimeout(function () { callback.apply(null, args); }, wait);
    };
  }

  function createElement(tag, className, text) {
    var element = document.createElement(tag);
    if (className) element.className = className;
    if (text) element.textContent = text;
    return element;
  }

  function highlight(text, query) {
    var fragment = document.createDocumentFragment();
    var source = String(text || '');
    var index = source.toLocaleLowerCase().indexOf(query.toLocaleLowerCase());
    if (index < 0) {
      fragment.appendChild(document.createTextNode(source));
      return fragment;
    }
    fragment.appendChild(document.createTextNode(source.slice(0, index)));
    var mark = document.createElement('mark');
    mark.textContent = source.slice(index, index + query.length);
    fragment.appendChild(mark);
    fragment.appendChild(document.createTextNode(source.slice(index + query.length)));
    return fragment;
  }

  function initSearch(section) {
    if (!section || section.dataset.searchReady === 'true') return;
    section.dataset.searchReady = 'true';

    var input = section.querySelector('[data-dyd-search-input]');
    var suggestions = section.querySelector('[data-dyd-search-suggestions]');
    var status = section.querySelector('[data-dyd-search-status]');
    var configNode = section.querySelector('[data-dyd-search-config]');
    var filterPanel = section.querySelector('[data-dyd-filter-panel]');
    var filterBackdrop = section.querySelector('[data-dyd-filter-backdrop]');
    var openFilters = section.querySelector('[data-dyd-open-filters]');
    var closeFilters = section.querySelector('[data-dyd-close-filters]');
    var filterForm = section.querySelector('[data-dyd-filter-form]');
    var sortSelect = section.querySelector('[data-dyd-sort]');
    var config = {};
    var request;
    var focusedIndex = -1;

    try { config = configNode ? JSON.parse(configNode.textContent) : {}; } catch (error) { config = {}; }

    function setExpanded(expanded) {
      if (!input || !suggestions) return;
      input.setAttribute('aria-expanded', String(expanded));
      suggestions.hidden = !expanded;
      if (!expanded) {
        focusedIndex = -1;
        input.removeAttribute('aria-activedescendant');
      }
    }

    function optionLinks() {
      return suggestions ? Array.prototype.slice.call(suggestions.querySelectorAll('[role="option"]')) : [];
    }

    function focusOption(nextIndex) {
      var links = optionLinks();
      if (!links.length) return;
      focusedIndex = (nextIndex + links.length) % links.length;
      links.forEach(function (link, index) {
        var isFocused = index === focusedIndex;
        link.classList.toggle('is-focused', isFocused);
        link.setAttribute('aria-selected', String(isFocused));
      });
      input.setAttribute('aria-activedescendant', links[focusedIndex].id);
      links[focusedIndex].scrollIntoView({ block: 'nearest' });
    }

    function addSuggestion(group, suggestion, query, type, index) {
      var link = createElement('a', 'dyd-search__suggestion');
      link.id = section.id + '-suggestion-' + type + '-' + index;
      link.href = suggestion.url || (config.searchUrl + '?q=' + encodeURIComponent(suggestion.text || suggestion.styled_text || '') + '&type=product&options%5Bprefix%5D=last');
      link.setAttribute('role', 'option');
      link.tabIndex = -1;

      if (suggestion.featured_image && suggestion.featured_image.url) {
        var image = document.createElement('img');
        image.src = suggestion.featured_image.url;
        image.alt = '';
        image.width = 54;
        image.height = 43;
        image.loading = 'lazy';
        link.appendChild(image);
      }

      var title = createElement('span');
      title.appendChild(highlight(suggestion.title || suggestion.text || suggestion.styled_text || '', query));
      link.appendChild(title);
      group.appendChild(link);
    }

    function addGroup(container, label, items, query, type) {
      if (!items || !items.length) return 0;
      var group = createElement('div', 'dyd-search__suggestion-group');
      group.appendChild(createElement('p', 'dyd-search__suggestion-label', label));
      items.forEach(function (item, index) { addSuggestion(group, item, query, type, index); });
      container.appendChild(group);
      return items.length;
    }

    function renderSuggestions(data, query) {
      var results = data && data.resources && data.resources.results ? data.resources.results : {};
      var fragment = document.createDocumentFragment();
      var count = 0;
      count += addGroup(fragment, config.queryLabel, results.queries, query, 'query');
      count += addGroup(fragment, config.productLabel, results.products, query, 'product');
      count += addGroup(fragment, config.collectionLabel, results.collections, query, 'collection');
      suggestions.replaceChildren(fragment);

      if (!count) {
        suggestions.appendChild(createElement('p', 'dyd-search__suggestion-empty', config.noSuggestions));
      }
      setExpanded(true);
      status.textContent = count ? config.suggestionsReady.replace('[count]', String(count)) : config.noSuggestions;
    }

    var fetchSuggestions = debounce(function () {
      if (!input || !suggestions) return;
      var query = input.value.trim();
      if (query.length < 2) {
        if (request) request.abort();
        suggestions.replaceChildren();
        setExpanded(false);
        status.textContent = '';
        return;
      }

      if (request) request.abort();
      request = new AbortController();
      status.textContent = config.loading;
      var endpoint = new URL(config.predictiveUrl, window.location.origin);
      endpoint.searchParams.set('q', query);
      endpoint.searchParams.set('resources[type]', 'query,product,collection');
      endpoint.searchParams.set('resources[limit]', '5');
      endpoint.searchParams.set('resources[limit_scope]', 'each');
      endpoint.searchParams.set('resources[options][unavailable_products]', 'last');

      fetch(endpoint.toString(), {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: request.signal
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Predictive search failed: ' + response.status);
          return response.json();
        })
        .then(function (data) { renderSuggestions(data, query); })
        .catch(function (error) {
          if (error.name === 'AbortError') return;
          suggestions.replaceChildren();
          setExpanded(false);
          status.textContent = config.error;
        });
    }, 180);

    if (input && suggestions && status) {
      input.addEventListener('input', fetchSuggestions);
      input.addEventListener('focus', function () {
        if (suggestions.children.length && input.value.trim().length >= 2) setExpanded(true);
      });
      input.addEventListener('keydown', function (event) {
        var links = optionLinks();
        if (event.key === 'ArrowDown' && links.length) {
          event.preventDefault();
          setExpanded(true);
          focusOption(focusedIndex + 1);
        } else if (event.key === 'ArrowUp' && links.length) {
          event.preventDefault();
          setExpanded(true);
          focusOption(focusedIndex - 1);
        } else if (event.key === 'Enter' && focusedIndex >= 0 && links[focusedIndex]) {
          event.preventDefault();
          links[focusedIndex].click();
        } else if (event.key === 'Escape') {
          setExpanded(false);
        }
      });
      document.addEventListener('click', function (event) {
        if (!event.target.closest('.dyd-search__form') && !event.target.closest('.dyd-search__suggestions')) setExpanded(false);
      });
    }

    function setFilterDrawer(open) {
      if (!filterPanel || !filterBackdrop) return;
      filterPanel.classList.toggle('is-open', open);
      filterBackdrop.classList.toggle('is-open', open);
      filterBackdrop.hidden = !open;
      document.body.classList.toggle('dyd-search-filters-open', open);
      if (open && closeFilters) closeFilters.focus();
      if (!open && openFilters) openFilters.focus();
    }

    if (openFilters) openFilters.addEventListener('click', function () { setFilterDrawer(true); });
    if (closeFilters) closeFilters.addEventListener('click', function () { setFilterDrawer(false); });
    if (filterBackdrop) filterBackdrop.addEventListener('click', function () { setFilterDrawer(false); });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && filterPanel && filterPanel.classList.contains('is-open')) setFilterDrawer(false);
    });

    if (filterForm) {
      filterForm.addEventListener('change', function (event) {
        if (event.target.matches('input[type="checkbox"]')) filterForm.requestSubmit();
      });
    }

    if (sortSelect && filterForm) {
      sortSelect.addEventListener('change', function () {
        var sortInput = filterForm.querySelector('input[name="sort_by"]');
        if (sortInput) sortInput.value = sortSelect.value;
        filterForm.requestSubmit();
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-dyd-premium-search]').forEach(initSearch);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.matches && event.target.matches('[data-dyd-premium-search]')
      ? event.target
      : event.target.querySelector('[data-dyd-premium-search]');
    if (section) initSearch(section);
  });
})();
